package nl.staartvin.solarpower.data.uv

/**
 * Approximate "safe time in sun before first sunburn" at a given UVI for a
 * given skin type, in whole minutes.
 *
 * Derivation:
 *   - UV index 1 corresponds to ~25 mW/m² of erythemally-weighted UV.
 *   - Type II baseline MED (minimal erythemal dose, the threshold for barely
 *     perceptible reddening) is ~200 J/m².
 *   - Time-to-MED at constant UVI Y is therefore 200 / (0.025 × Y) = 8000/Y
 *     seconds, or ~133/Y minutes.
 *   - Scale by [SkinType.medFactor] for other phototypes.
 *
 * Edge cases:
 *   - UVI ≤ 0 → null ("essentially unlimited" — typically night).
 *   - Extremely low UVI (< [MIN_RELEVANT_UVI]) → null; computed value would
 *     be hours and not actionable.
 *   - Result is rounded down to whole minutes for a conservative display
 *     ("you have ≥ N minutes" rather than "exactly N minutes 30 seconds").
 *
 * This is a HEURISTIC, not medical advice. The UI surfaces that caveat.
 */
fun safeTimeMinutes(uvi: Double, skinType: SkinType): Int? =
    safeTimeMinutesExact(uvi, skinType)?.toInt()?.coerceAtLeast(1)

/**
 * Same math as [safeTimeMinutes] but returns the exact Double so downstream
 * callers (e.g. dose-remaining calculations) don't lose precision to the
 * whole-minute rounding. Internal to :shared.
 */
internal fun safeTimeMinutesExact(uvi: Double, skinType: SkinType): Double? {
    if (uvi <= MIN_RELEVANT_UVI) return null
    return MED_BASELINE_MINUTES * skinType.medFactor / uvi
}

internal const val MED_BASELINE_MINUTES = 133.0
internal const val MED_BASELINE_J_PER_M2 = 200.0  // Type II baseline; scaled by medFactor.
internal const val MIN_RELEVANT_UVI = 0.5

/**
 * Human-friendly duration for safe-sun readouts. Once sunscreen stretches
 * the budget into hours, "~960 min" is absurd — switch to hours, and past
 * a full day just say "all day".
 *
 *   0..89     → "~45 min"
 *   90..599   → "~1.5 hr" / "~3 hr"   (rounded to the nearest half hour)
 *   600+      → "all day"
 *
 * Lives in shared so Android and iOS phrase it identically. English-only
 * for now — a real localization pass would move this per-platform.
 */
fun formatSafeDuration(minutes: Int): String = when {
    minutes >= 600 -> "all day"
    minutes >= 90 -> {
        val halfHours = (minutes + 15) / 30            // round to nearest 0.5 h
        val hours = halfHours / 2.0
        val whole = hours.toLong()
        if (hours == whole.toDouble()) "~$whole hr" else "~$hours hr"
    }
    else -> "~$minutes min"
}
